import { useCallback, useEffect, useRef } from "react";

// App
import { serverUrl } from "../../config";

const VISITOR_KEY = "dsr_visitor_id";

/**
 * Returns a stable anonymous visitor id, minted on first visit.
 */
export const getVisitorId = () => {
  let visitorId = null;
  try {
    visitorId = localStorage.getItem(VISITOR_KEY);
  } catch (e) {
    // Storage unavailable (private mode) - fall through to a session id.
  }

  if (!visitorId) {
    visitorId =
      typeof crypto !== "undefined" && crypto.randomUUID
        ? crypto.randomUUID().replace(/-/g, "")
        : `v${Date.now().toString(36)}${Math.random().toString(36).slice(2, 12)}`;
    try {
      localStorage.setItem(VISITOR_KEY, visitorId);
    } catch (e) {
      // Best effort - an unsaved id still tracks within this page view.
    }
  }

  return visitorId;
};

/**
 * Buyer engagement capture for the room portal.
 *
 * All requests are sent with Content-Type: text/plain on purpose: the portal
 * runs on a different origin than the CMS, and a "safelisted" content type
 * keeps both fetch(keepalive) and sendBeacon free of CORS preflights. The
 * backend decodes the raw JSON body regardless of the header.
 *
 * `disabled` (seller preview mode) silences every event - previews must
 * never pollute the room's analytics.
 */
export default function useRoomTracking(token, disabled = false) {
  const timerRef = useRef(null);

  const trackUrl = `${serverUrl}/api/v3/room/${token}/track`;

  const track = useCallback(
    (event) => {
      if (!token || disabled) {
        return;
      }
      fetch(trackUrl, {
        method: "POST",
        keepalive: true,
        headers: { "Content-Type": "text/plain" },
        body: JSON.stringify({ visitor_id: getVisitorId(), ...event }),
      }).catch(() => {});
    },
    // eslint-disable-next-line
    [token, disabled]
  );

  const startContentTimer = useCallback(
    (item) => {
      if (disabled) {
        return;
      }
      timerRef.current = { item, startedAt: Date.now() };
    },
    [disabled]
  );

  const stopContentTimer = useCallback(() => {
    if (!timerRef.current) {
      return;
    }
    const { item, startedAt } = timerRef.current;
    timerRef.current = null;

    track({
      event_type: "content_viewed",
      content_type: item.type,
      content_id: Number(item.id),
      duration: Math.round((Date.now() - startedAt) / 1000),
    });
  }, [track]);

  // Flush a running content timer when the tab hides or unloads.
  useEffect(() => {
    const flush = () => {
      if (!timerRef.current || !token || disabled) {
        return;
      }
      const { item, startedAt } = timerRef.current;
      timerRef.current = null;

      const payload = JSON.stringify({
        visitor_id: getVisitorId(),
        events: [
          {
            event_type: "content_viewed",
            content_type: item.type,
            content_id: Number(item.id),
            duration: Math.round((Date.now() - startedAt) / 1000),
          },
        ],
      });

      if (navigator.sendBeacon) {
        navigator.sendBeacon(
          trackUrl,
          new Blob([payload], { type: "text/plain" })
        );
      }
    };

    const onVisibilityChange = () => {
      if (document.visibilityState === "hidden") {
        flush();
      }
    };

    window.addEventListener("pagehide", flush);
    document.addEventListener("visibilitychange", onVisibilityChange);

    return () => {
      window.removeEventListener("pagehide", flush);
      document.removeEventListener("visibilitychange", onVisibilityChange);
    };
    // eslint-disable-next-line
  }, [token, disabled]);

  return { track, startContentTimer, stopContentTimer };
}
